import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SchedaLockup } from "@/components/SchedaLockup";
import { Footer } from "../landing/Footer";
import { LogoMark } from "../landing/Navbar";
import "../landing/landing.css";

/** Which product's brand heads the page. See {@link LegalPageLayout}. */
export type LegalBrand = "scheda" | "delgentic";

/**
 * Shell for the public legal pages (Privacy, Cookies). Renders OUTSIDE the
 * AuthGate (see App.tsx) so anonymous visitors reach it from the landing
 * footer. Reuses the light, marketing-surface styling (`.landing` scope:
 * Inter / Space Grotesk, slate palette) and the landing Footer, so it reads
 * as part of the same site rather than the authenticated comic app.
 *
 * <p>{@code onSignup} is injected rather than pulled from {@code useAuth()}:
 * these pages are also rendered by the standalone catalog bundle at the apex
 * (delgentic.com), which has no OIDC provider — there, signing up means
 * redirecting to the app at scheda. In the SPA, App.tsx passes the usual
 * {@code signinRedirect}.</p>
 *
 * <h2>Why {@code brand} is a required prop</h2>
 * One component, one copy source, but THREE origins: the app bundle serves
 * scheda.delgentic.com (plus the legacy a2a alias) and the catalog bundle serves
 * the apex. The page should be headed by whichever brand the visitor arrived
 * under, so the app bundle passes {@code "scheda"} and the catalog passes
 * {@code "delgentic"}. Branching on the BUNDLE rather than the hostname is what
 * makes the legacy alias correct for free.
 *
 * <p>It is required, not defaulted, so TypeScript forces every call site to
 * choose — a silent default is how the apex would quietly start advertising a
 * product logo. And it is a plain prop, not a context or a
 * {@code window.location} sniff, because nothing reachable from the apex may
 * call a hook that needs a provider (see {@code catalog/CatalogApp.tsx}; this
 * very file caused that outage once already).</p>
 *
 * <p>The body copy does not vary. Scheda is a Delgentic product and the data
 * controller is Delgentic ApS on either origin.</p>
 */
export function LegalPageLayout({
  title,
  updated,
  brand,
  onSignup,
  children,
}: {
  title: string;
  updated: string;
  brand: LegalBrand;
  onSignup: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="landing min-h-screen">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <Link
            to="/"
            aria-label={brand === "scheda" ? "Scheda, home" : undefined}
            className="flex items-center gap-2 font-display text-lg font-semibold tracking-tight text-slate-900"
          >
            {brand === "scheda" ? (
              // surface="light": this page is white in every theme, so the
              // reversed (white) wordmark would be invisible here.
              <SchedaLockup className="h-7 w-auto" surface="light" alt="" />
            ) : (
              <>
                <LogoMark />
                {t("legal.brand")}
              </>
            )}
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">
              {t("legal.back")}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {title}
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          {t("legal.updated", { date: updated })}
        </p>
        <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-slate-600">
          {children}
        </div>
      </main>

      <Footer onSignup={onSignup} />
    </div>
  );
}

/** A titled section within a legal page. */
export function Section({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold text-slate-900">
        {heading}
      </h2>
      {children}
    </section>
  );
}

/** Bulleted list with the page's spacing/typography. */
export function List({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-1.5 pl-5">{children}</ul>;
}
